import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { baseURLPublic } from '../../config/BaseUrl';
import PatronPage from './PatronPage';
import LeadForm from './LeadForm';
import OfferPanel from './OfferPanel';
import ExperienceShell from './ExperienceShell';
import { requestKey, trackLeadAction } from './leadClient';

const linkPath = (token) => `/social/gallery/v2/share/${encodeURIComponent(token)}`;
const storageKey = (token) => `sg-guest:${token}`;

export default function RecipientMedia({ match }) {
  const token = match.params.token;
  const history = useHistory();
  const [state, setState] = useState({ loading: true });
  const [retry, setRetry] = useState(0);
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState('');
  const sequence = useRef(0);
  const [action, setAction] = useState(null);
  const [actionSent, setActionSent] = useState(false);
  const [actionError, setActionError] = useState('');
  const [redirecting, setRedirecting] = useState(false);
  const redirectKey = useRef(null);
  const [offer, setOffer] = useState(null);
  const [offerBusy, setOfferBusy] = useState(false);
  const [offerError, setOfferError] = useState('');
  const offerRequest = useRef(0);
  const offerInFlight = useRef(false);
  const [readyToken, setReadyToken] = useState(null);
  const viewed = useRef(false);
  const opened = useRef(null);

  useEffect(() => {
    const request = ++sequence.current;
    setState({ loading: true }); setSwitchError(''); setSwitching(false);
    setAction(null); setActionSent(false); setActionError(''); setRedirecting(false); redirectKey.current = null;
    offerRequest.current += 1; offerInFlight.current = false;
    setOffer(null); setOfferBusy(false); setOfferError('');
    setReadyToken(null); opened.current = null; viewed.current = false;
    // A deliberate guest choice applies to this tab, not to other viewers of the original link.
    let guestToken;
    try { guestToken = sessionStorage.getItem(storageKey(token)); } catch (error) { /* URL replacement still preserves guest reloads. */ }
    if (guestToken && /^[A-Za-z0-9_-]{43}$/.test(guestToken)) {
      history.replace(linkPath(guestToken));
    } else {
      baseURLPublic.get(`social-gallery-v2/links/${encodeURIComponent(token)}`).then(({ data }) => {
        if (sequence.current === request) setState({ data: data.data, token });
      }).catch((error) => {
        if (sequence.current === request) setState({ error: error.response?.status === 404 ?
          'This link is unavailable.' : 'We could not load your media. Please try again.' });
      });
    }
    return () => { sequence.current += 1; };
  }, [token, retry, history]);

  useEffect(() => {
    const recordVisible = () => {
      if (document.visibilityState === 'hidden' || state.token !== token || !state.data?.tracking_enabled) return;
      if (readyToken === token && opened.current !== token) {
        opened.current = token; trackLeadAction(token, 'social_gallery_opened');
      }
      if (action === 'offer' && offer?.offer && !viewed.current) {
        viewed.current = true; trackLeadAction(token, 'return_offer_viewed');
      }
    };
    recordVisible(); document.addEventListener('visibilitychange', recordVisible);
    return () => document.removeEventListener('visibilitychange', recordVisible);
  }, [token, state, readyToken, action, offer]);

  useEffect(() => {
    const anchor = history.location.hash.slice(1);
    if (state.token !== token || !['offer', 'function', 'membership'].some((id) => anchor === `sg-patron-action-${id}`)) return;
    const target = document.getElementById(anchor);
    target?.scrollIntoView?.({ block: 'center' });
    target?.focus?.({ preventScroll: true });
  }, [state.token, token, history.location.hash]);

  const trackMediaAction = (name) => {
    if (name === 'gallery_clicked' && state.token === token && state.data?.gallery_visible) {
      try { sessionStorage.setItem(`sg-gallery-context:${state.data.event_id}`, token); } catch (_) { /* Gallery works without optional action shortcuts. */ }
    }
    if (state.token === token && state.data?.tracking_enabled) trackLeadAction(token, name);
  };

  const continueAsGuest = async () => {
    const request = sequence.current;
    setSwitching(true); setSwitchError(''); setAction(null); setActionError('');
    offerRequest.current += 1; offerInFlight.current = false; setOffer(null); setOfferBusy(false); setOfferError('');
    try {
      const { data } = await baseURLPublic.post(`social-gallery-v2/links/${encodeURIComponent(token)}/guest`, {});
      if (sequence.current !== request) return;
      const guestToken = data.data.token;
      if (!/^[A-Za-z0-9_-]{43}$/.test(guestToken)) throw new Error('Invalid guest link');
      try { sessionStorage.setItem(storageKey(token), guestToken); } catch (error) { /* Keep identity in the new URL when storage is unavailable. */ }
      history.replace(linkPath(guestToken));
    } catch (error) {
      if (sequence.current === request) setSwitchError('We could not switch to guest mode. Please try again.');
    } finally {
      if (sequence.current === request) setSwitching(false);
    }
  };

  const offerOperation = async (operation, phone) => {
    if (offerInFlight.current) return;
    offerInFlight.current = true;
    const request = ++offerRequest.current;
    const context = sequence.current;
    setOfferBusy(true); setOfferError('');
    try {
      const { data } = await baseURLPublic.post(`social-gallery-v2/links/${encodeURIComponent(token)}/offer/${operation}`,
        operation === 'redeem' ? { staff_confirmed: true } : operation === 'reminder' ? { consent: true } : phone ? { phone } : {});
      if (context === sequence.current && request === offerRequest.current) setOffer(old => operation === 'reminder' ? { ...old, reminder: data.data.reminder } : data.data);
    } catch (failure) {
      if (context === sequence.current && request === offerRequest.current) setOfferError(
        failure.response?.data?.errors?.phone?.[0] || 'Could not check your offer. Please try again.');
    } finally {
      if (context === sequence.current && request === offerRequest.current) { offerInFlight.current = false; setOfferBusy(false); }
    }
  };

  const openAction = async (selected) => {
    if (switching || redirecting) return;
    const request = sequence.current;
    setActionError(''); setActionSent(false); window.scrollTo?.(0, 0);
    if (selected.id === 'offer') {
      viewed.current = false; trackMediaAction('return_offer_clicked');
      setOffer(null); setAction('offer'); offerOperation('claim'); return;
    }
    trackLeadAction(token, selected.id === 'function' ? 'function_enquiry_clicked' : 'membership_clicked');
    if (selected.mode !== 'external') { setAction(selected.id); return; }
    setRedirecting(true);
    if (!redirectKey.current) redirectKey.current = requestKey();
    try {
      const { data } = await baseURLPublic.post(`social-gallery-v2/links/${encodeURIComponent(token)}/membership-redirect`, { request_key: redirectKey.current });
      if (sequence.current === request && /^https?:\/\//i.test(data.data.url)) window.location.assign(data.data.url);
    } catch (error) { if (sequence.current === request) setActionError('Could not open the membership page. Please try again.'); }
    finally { if (sequence.current === request) setRedirecting(false); }
  };
  const closeAction = () => {
    const previous = action; setAction(null);
    offerRequest.current += 1; offerInFlight.current = false; setOfferBusy(false); setOfferError('');
    window.setTimeout(() => document.querySelector(`[data-sg-action="${previous}"]`)?.focus(), 0);
  };

  const referrerPolicy = <Helmet><meta name="referrer" content="no-referrer" /></Helmet>;
  if (state.loading || (!state.error && state.token !== token)) return <>{referrerPolicy}<main className="sg-public-state" role="status">Loading your moment…</main></>;
  if (state.error) return <>{referrerPolicy}<main className="sg-public-state"><p role="alert">{state.error}</p><button type="button" onClick={() => setRetry(retry + 1)}>Try again</button></main></>;

  const identityControl = state.data.context_type === 'guest' ? <p role="status">You’re continuing as a guest.</p> :
    <><button hidden type="button" className="sg-guest-switch" disabled={switching} onClick={continueAsGuest}>{switching ? 'Switching…' : "This isn't my link"}</button>
      {switchError && <p className="sg-guest-error" role="alert">{switchError}</p>}</>;
  if (action) return <>{referrerPolicy}<ExperienceShell data={state.data} view={actionSent ? 'sent' : action === 'offer' && offer?.status === 'redeemed' ? 'redeemed' : action} onClose={closeAction}>
    {action === 'offer' ? <OfferPanel key={`${token}:offer`} data={offer} busy={offerBusy} error={offerError} venueName={state.data.venue_name}
      onClaim={phone => offerOperation('claim', phone)} onRedeem={() => offerOperation('redeem')} onReminder={() => offerOperation('reminder')} onClose={closeAction} /> :
      <LeadForm key={`${token}:${action}`} token={token} type={action} venueName={state.data.venue_name} onSaved={() => setActionSent(true)} onClose={closeAction} />}
    <div className="sgx-identity">{identityControl}</div>
  </ExperienceShell></>;
  return <>{referrerPolicy}<PatronPage data={state.data} identityControl={identityControl} onAction={openAction} actionsDisabled={switching || redirecting || offerBusy}
    onMediaReady={() => setReadyToken(token)} onMediaAction={trackMediaAction} />
    {actionError && <p className="sg-action-error" role="alert">{actionError}</p>}{redirecting && <p className="sg-action-error" role="status">Opening membership page…</p>}
  </>;
}
